import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { PaxtonWordmark, SidePeekLeft } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { kDefaultLogoPath } from '@/constants/constants-ui'

export type SideNavHeaderProps = {
  /** URL for the logo image */
  logoUrl: string
  /** Alt text for the logo */
  logoAlt?: string
  /** Where clicking the logo navigates to */
  logoHref?: string
  /** Whether to apply fixed height sizing to the logo (34px) */
  hasFixedLogoHeight?: boolean
  /** Optional callback when logo is clicked (e.g., to close mobile sidebar) */
  onLogoClick?: () => void
  /** Contracts the nav to the icon rail. Omit to render no toggle (mobile, flag off). */
  onContract?: () => void
  /** Ref to the contract toggle, so the owner can move focus here when the nav expands. */
  contractToggleRef?: React.Ref<HTMLButtonElement>
  /* Whether the nav is rendered in the slideover */
  isSlideover?: boolean
}

/**
 * Header section of the SideNav containing the logo.
 *
 * Features:
 * - Logo with link to dashboard
 * - Optional contract toggle, the counterpart to SideNavRail's expand toggle
 * - Bottom-right corner gap that lines up with the page header border (closed in the slideover)
 * - Fixed 60px height
 */
export function SideNavHeader({
  logoUrl,
  logoAlt = 'Logo',
  logoHref = '/dashboard',
  hasFixedLogoHeight = false,
  onLogoClick,
  onContract,
  contractToggleRef,
  isSlideover = false,
}: SideNavHeaderProps) {
  return (
    <div className="flex relative h-[60px]">
      {/* No right padding: the border-drawing sibling below supplies the gap to the nav edge. */}
      <div className="w-full flex items-center justify-between gap-2 pl-6">
        <Link to={logoHref} onClick={onLogoClick}>
          {logoUrl === kDefaultLogoPath ? (
            <PaxtonWordmark
              role="img"
              aria-label={logoAlt}
              className={clsx('shrink-0 text-brand-500', hasFixedLogoHeight ? 'h-[34px] w-[113px]' : 'h-[45px] w-[150px] max-h-[56px] max-w-[150px]')}
            />
          ) : (
            <img className={clsx('max-w-[150px] max-h-[56px]', hasFixedLogoHeight && 'h-[34px] w-auto')} src={logoUrl} alt={logoAlt} />
          )}
        </Link>
        {onContract && (
          <IconButton
            ref={contractToggleRef}
            aria-label="Collapse navigation"
            tooltipText="Collapse"
            disableTooltipInteractive
            aria-expanded
            icon={<SidePeekLeft className="h-5 w-5" />}
            onClick={onContract}
            size="sm"
            variant="plain"
            color="neutral"
            className="rounded-md"
          />
        )}
      </div>
      {/* Narrow div drawing the right border, aligned with the navigation below */}
      {/* Outside the slideover, both borders stop short, leaving a corner gap that lines up with the page header border */}
      <div className={clsx('w-4 border-r', !isSlideover && 'mb-4')} />
      <div className={clsx('absolute bottom-0 left-0 border-b border-border-default', isSlideover ? 'right-0' : 'right-4')} />
    </div>
  )
}
